/**
 * OPUS-913 — small interactive inputs: Toggle, Segmented, SearchInput.
 * Ported from the Claude Design handoff (app/components.jsx).
 */
import React, { useState } from "react";
import { Icon } from "./Icon";
import { IconButton } from "./Button";

/** Toggle switch — coral when on (design rule: toggle "on" uses the brand accent). */
export function Toggle({ checked, onChange, label, disabled }) {
  return (
    <button
      type="button" role="switch" aria-checked={checked} aria-label={label} disabled={disabled}
      onClick={() => !disabled && onChange && onChange(!checked)}
      style={{
        display: "inline-flex", alignItems: "center", width: 38, height: 22, borderRadius: 999,
        background: checked ? "var(--coral)" : "var(--surface-3)", border: "none", padding: 2,
        cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1, transition: "background .16s", flexShrink: 0,
      }}
    >
      <span style={{
        width: 18, height: 18, borderRadius: 999, background: "#fff",
        transform: checked ? "translateX(16px)" : "translateX(0)", transition: "transform .16s",
        boxShadow: "0 1px 2px rgba(0,0,0,.4)",
      }} />
    </button>
  );
}

/** Segmented control — used for the renderer-list view switcher, etc. */
export function Segmented({ options, value, onChange, size = "md" }) {
  return (
    <div style={{ display: "inline-flex", padding: 3, gap: 2, background: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: "var(--r-md)" }}>
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button key={o.value} type="button" onClick={() => onChange(o.value)} title={o.title}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6, padding: size === "sm" ? "5px 9px" : "6px 12px",
              fontSize: size === "sm" ? 12 : 13, fontWeight: 600, fontFamily: "var(--font-sans)", border: "none",
              borderRadius: "var(--r-sm)", cursor: "pointer", transition: "all .13s",
              background: active ? "var(--surface-4)" : "transparent", color: active ? "var(--text)" : "var(--text-dim)",
              boxShadow: active ? "0 1px 2px rgba(0,0,0,.3)" : "none",
            }}>
            {o.icon && <Icon name={o.icon} size={14} />}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export function SearchInput({ value, onChange, placeholder = "Search…", width = 280 }) {
  const [focus, setFocus] = useState(false);
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 8, width, height: 38, padding: "0 12px",
      background: "var(--surface-2)", border: `1px solid ${focus ? "var(--coral)" : "var(--border)"}`,
      borderRadius: "var(--r-md)", transition: "border-color .14s", boxSizing: "border-box",
    }}>
      <Icon name="search" size={16} style={{ color: "var(--text-dim)" }} />
      <input
        value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
        style={{ flex: 1, minWidth: 0, background: "transparent", border: "none", outline: "none", color: "var(--text)", fontSize: 13.5, fontFamily: "var(--font-sans)" }}
      />
      {value ? <IconButton icon="x" size={13} title="Clear" onClick={() => onChange("")} /> : null}
    </div>
  );
}

export default Toggle;
